/**
 * UnmatchedFaxes — fax triage queue (the "Unmatched" sub-tab of the Fax tab,
 * formerly the standalone /crm/records page).
 *
 * Inbound faxes that couldn't be auto-matched (no reference code, ambiguous
 * sender) land here. Triage is a QUEUE: two-pane on lg (list left, preview
 * right), stacked on mobile (tap into a full-screen preview with a back
 * link); rows OLDEST FIRST — work the backlog FIFO. The match panel renders
 * the system's sender-number suggestion chips first (the 90% case), then a
 * contact typeahead over the existing GET /contacts?q=. Confirming a match
 * auto-advances to the next item.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, Inbox, Loader2 } from 'lucide-react';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import api from '../../../services/api';
import { ContactListItem, FaxInboxJob } from '../../types';
import { contactName } from '../../types';
import { formatPhone, notifyStatsChanged, prettySlug, relTime } from '../../lib';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

export default function UnmatchedFaxes() {
  const [jobs, setJobs] = useState<FaxInboxJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobilePreview, setMobilePreview] = useState(false);

  // Preview presign (fetched on selection only — never in the list payload).
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const previewForRef = useRef<string | null>(null);

  // Match panel state.
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ContactListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selected = jobs.find((j) => j.id === selectedId) || null;

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const res = await api.get('/api/crm/fax/inbox');
      const rows: FaxInboxJob[] = res.data?.jobs || [];
      setJobs(rows);
      setError(null);
      setSelectedId((prev) => (prev && rows.some((j) => j.id === prev) ? prev : rows[0]?.id || null));
    } catch (e: any) {
      if (initial) setError(errMsg(e));
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => { load(true); }, [load]);

  // Fetch the selected fax's PDF BYTES through the backend and render a blob
  // (row-expand only). The PHI bucket has no browser CORS, so handing react-pdf
  // a presigned S3 URL fails silently — the proxy returns same-origin bytes.
  useEffect(() => {
    setPdfUrl(null);
    setNumPages(0);
    setConfirmDiscard(false);
    setActionError(null);
    setQuery('');
    setResults([]);
    if (!selected?.document?.id) return;
    const docId = selected.document.id;
    previewForRef.current = selected.id;
    setPdfLoading(true);
    let objectUrl: string | null = null;
    api.get(`/api/crm/documents/${docId}/file`, { responseType: 'blob' })
      .then((res) => {
        if (previewForRef.current === selected.id) {
          objectUrl = URL.createObjectURL(res.data);
          setPdfUrl(objectUrl);
        }
      })
      .catch(async (e) => {
        // With responseType:'blob', axios delivers JSON error bodies as a Blob,
        // so errMsg's data.detail/error are undefined — read the blob to recover
        // the server's actual reason.
        let msg = errMsg(e);
        const d = e?.response?.data;
        if (d instanceof Blob) {
          try {
            const parsed = JSON.parse(await d.text());
            msg = parsed.detail || parsed.error || msg;
          } catch { /* keep the generic message */ }
        }
        if (previewForRef.current === selected.id) setActionError(msg);
      })
      .finally(() => {
        if (previewForRef.current === selected.id) setPdfLoading(false);
      });
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Debounced contact typeahead (~300ms) over the existing list endpoint.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/api/crm/contacts', { params: { q, limit: 8 } });
        setResults(res.data?.contacts || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [query]);

  /** Confirm a match, then auto-advance to the next needs_match item. */
  const matchTo = async (contactId: string) => {
    if (!selected || actionBusy) return;
    setActionBusy(true);
    setActionError(null);
    const matchedId = selected.id;
    try {
      await api.post(`/api/crm/fax/${matchedId}/match`, { contact_id: contactId });
      notifyStatsChanged();
      const remaining = jobs.filter((j) => j.id !== matchedId);
      setJobs(remaining);
      setSelectedId(remaining[0]?.id || null);
      if (!remaining.length) setMobilePreview(false);
    } catch (e: any) {
      if (e?.response?.status === 409) {
        // Someone else handled it — drop it and move on.
        await load(false);
      } else {
        setActionError(errMsg(e));
      }
    } finally {
      setActionBusy(false);
    }
  };

  const discard = async () => {
    if (!selected || actionBusy) return;
    setActionBusy(true);
    setActionError(null);
    const discardedId = selected.id;
    try {
      await api.post(`/api/crm/fax/${discardedId}/discard`);
      notifyStatsChanged();
      const remaining = jobs.filter((j) => j.id !== discardedId);
      setJobs(remaining);
      setSelectedId(remaining[0]?.id || null);
      setConfirmDiscard(false);
      if (!remaining.length) setMobilePreview(false);
    } catch (e: any) {
      if (e?.response?.status === 409) await load(false);
      else setActionError(errMsg(e));
    } finally {
      setActionBusy(false);
    }
  };

  const listPane = (
    <div className="space-y-1.5">
      {jobs.map((j) => (
        <div
          key={j.id}
          role="button"
          tabIndex={0}
          onClick={() => { setSelectedId(j.id); setMobilePreview(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target === e.currentTarget) {
              setSelectedId(j.id);
              setMobilePreview(true);
            }
          }}
          className={`cursor-pointer rounded-lg border p-3 transition-colors duration-150 min-h-[44px] ${
            j.id === selectedId
              ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20'
              : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/60'
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
              {formatPhone(j.from_number) || 'Unknown sender'}
            </span>
            <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              needs match
            </span>
          </div>
          <p className="mt-0.5 text-xs text-gray-400">
            {relTime(j.created_at)}{j.page_count ? ` · ${j.page_count} page${j.page_count === 1 ? '' : 's'}` : ''}
          </p>
        </div>
      ))}
    </div>
  );

  const previewPane = selected ? (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Assign this fax</h3>
        {actionError && (
          <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {actionError}
          </div>
        )}
        {selected.candidates.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              We've faxed this office before for:
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {selected.candidates.map((c) => (
                <button
                  key={c.id}
                  onClick={() => matchTo(c.id)}
                  disabled={actionBusy}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700 transition-colors duration-150 hover:bg-primary-200 disabled:opacity-50 dark:bg-primary-900/40 dark:text-primary-300 dark:hover:bg-primary-900/60 sm:min-h-0"
                >
                  Assign to {c.name}
                  {c.phone && <span className="font-normal text-primary-600 dark:text-primary-400">{formatPhone(c.phone)}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="mt-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search patients by name, phone, or email…"
            aria-label="Search patients to assign this fax"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          {searching && <Loader2 className="mt-2 h-4 w-4 animate-spin text-gray-400" />}
          {!searching && results.length > 0 && (
            <div className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
              {results.map((c) => (
                <button
                  key={c.id}
                  onClick={() => matchTo(c.id)}
                  disabled={actionBusy}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors duration-150 hover:bg-gray-50 disabled:opacity-50 dark:hover:bg-gray-700/60 min-h-[44px]"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">{contactName(c)}</span>
                    <span className="block text-xs text-gray-400">{formatPhone(c.phone_e164)}</span>
                    {/* Study chips so same-name patients are distinguishable. */}
                    {(c.studies || []).length > 0 && (
                      <span className="mt-0.5 flex flex-wrap gap-1">
                        {(c.studies || []).slice(0, 3).map((s, i) => (
                          <span key={i} className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            {s.study_internal_title || s.study_title || prettySlug(s.study_slug || '')}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs font-medium text-primary-700 dark:text-primary-400">Assign</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-3 border-t border-gray-100 pt-3 dark:border-gray-700">
          {confirmDiscard ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-600 dark:text-gray-300">
                Discard this fax? It stays stored and auditable, but leaves the queue.
              </span>
              <button onClick={discard} disabled={actionBusy} className="min-h-[44px] rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50 sm:min-h-0">
                {actionBusy ? 'Discarding…' : 'Discard'}
              </button>
              <button onClick={() => setConfirmDiscard(false)} className="min-h-[44px] text-xs font-medium text-gray-500 hover:underline dark:text-gray-400 sm:min-h-0">
                Keep it
              </button>
            </div>
          ) : (
            <button onClick={() => setConfirmDiscard(true)} className="min-h-[44px] text-xs font-medium text-gray-500 hover:underline dark:text-gray-400 sm:min-h-0">
              Discard this fax
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40">
        {pdfLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : pdfUrl ? (
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            onLoadError={() => setActionError('Could not render the fax PDF — try reselecting it.')}
            loading={
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            }
          >
            {Array.from({ length: numPages }, (_, i) => (
              <div key={i} className="border-b border-gray-200 last:border-0 dark:border-gray-700">
                <Page pageNumber={i + 1} width={Math.min(window.innerWidth - 48, 760)} renderTextLayer={false} renderAnnotationLayer={false} />
              </div>
            ))}
          </Document>
        ) : (
          <p className="py-16 text-center text-sm text-gray-400">No preview available.</p>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Inbound faxes that couldn't be matched automatically. Work the list oldest-first.
      </p>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
          <button onClick={() => load(true)} className="ml-2 min-h-[44px] font-medium underline sm:min-h-0">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <Inbox className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
          <p className="mt-3 text-sm font-medium text-gray-900 dark:text-gray-100">No faxes waiting for a match.</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Inbound records that can't be auto-matched land here.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop: two-pane */}
          <div className="hidden gap-5 lg:flex" style={{ minHeight: '60vh' }}>
            <div className="w-80 shrink-0 overflow-y-auto">{listPane}</div>
            {previewPane}
          </div>
          {/* Mobile: list, tap into a full preview */}
          <div className="lg:hidden">
            {mobilePreview && selected ? (
              <div className="space-y-3">
                <button
                  onClick={() => setMobilePreview(false)}
                  className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-primary-700 dark:text-primary-400"
                >
                  <ChevronLeft className="h-4 w-4" /> Back to the queue
                </button>
                {previewPane}
              </div>
            ) : (
              listPane
            )}
          </div>
        </>
      )}
    </div>
  );
}
